import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TourService } from 'ngx-ui-tour-md-menu';
import { ProjectService } from './project.service';
import { SharedService } from './sharedService';
import { TourConfigService } from './tour-config.service';
import { SweetAlertService } from './sweet-alert.service';
import { TokenEncryptionService } from './token-encryption.service';
import { hasCompanyRole } from '../utils/role.utils';
import { OnboardingChecklistService } from '../onboarding-checklist/onboarding-checklist.service';
import { ActivationJourneyService } from '../onboarding-checklist/activation-journey.service';

/**
 * Shared non-blocking actions for trial onboarding help (setup wizard, product tour, demo dashboard).
 * Keeps behavior aligned across dashboard banner, header menu, and empty states.
 *
 * Legacy dashboard sample opt-in (`supersee_demo_dashboard_opt_in`) is separate from the
 * guided Example workday at `/setup/example-workday` (scoped_example). Never enable global
 * sample mode during `/setup/*`.
 */
@Injectable({ providedIn: 'root' })
export class TrialOnboardingHelpActionsService {
  readonly demoDashboardOptInKey = 'supersee_demo_dashboard_opt_in';

  constructor(
    private router: Router,
    private projectService: ProjectService,
    private sharedServices: SharedService,
    private tourConfigService: TourConfigService,
    private tourMdMenuService: TourService,
    private sweetAlertService: SweetAlertService,
    private tokenEncryptionService: TokenEncryptionService,
    private onboardingService: OnboardingChecklistService,
    private activationJourney: ActivationJourneyService
  ) {}

  canShowOptionalHelpLinks(): boolean {
    if (
      this.activationJourney.isEnabled &&
      this.activationJourney.getState().privateEvaluationActive
    ) {
      return false;
    }
    const user =
      this.tokenEncryptionService.getEncryptedItem<any>('user') ||
      this.tokenEncryptionService.getEncryptedItem<any>('logedInUser');

    if (!hasCompanyRole(user)) return false;

    const quotaStatus = user?.meta?.quota_status;
    const isPaid = quotaStatus === 'active' || quotaStatus === 'Active';

    return !isPaid;
  }

  isShowingDemoData(): boolean {
    return localStorage.getItem(this.demoDashboardOptInKey) === 'true';
  }

  clearDashboardSampleOptIn(): void {
    const wasOptedIn = this.isShowingDemoData();
    localStorage.removeItem(this.demoDashboardOptInKey);
    localStorage.removeItem('sampleDataBannerDismissed');
    // Only notify listeners when opt-in actually changed. Unconditional refresh
    // re-enters Screenshots getUserList → clearLegacySampleMode → refresh forever.
    if (wasOptedIn) {
      this.sharedServices.requestDashboardDemoOptInRefresh();
    }
  }

  /**
   * Clear stale global sample opt-in after paid upgrade or when real employee data exists.
   */
  clearStaleSampleOptIn(opts?: { hasRealEmployeeData?: boolean; isPaid?: boolean }): void {
    if (!this.isShowingDemoData()) {
      return;
    }
    const user =
      this.tokenEncryptionService.getEncryptedItem<any>('user') ||
      this.tokenEncryptionService.getEncryptedItem<any>('logedInUser');
    const quotaStatus = String(user?.meta?.quota_status || '').toLowerCase();
    const paid = opts?.isPaid ?? (quotaStatus === 'active');
    if (paid || opts?.hasRealEmployeeData) {
      this.clearDashboardSampleOptIn();
    }
  }

  isOnboardingCompleted(): boolean {
    return this.onboardingService.isAllCompleted();
  }

  resumeSetup(): void {
    this.router.navigate(['/setup'], { queryParams: { t: Date.now() } });
  }

  /**
   * Toggle legacy empty-account dashboard sample data.
   * @param enable When true, opt in and show sample dashboard; when false, opt out.
   * Never enables during `/setup/*` — use the guided Example workday instead.
   */
  tryDemoData(enable = false): void {
    if (
      enable &&
      this.activationJourney.isEnabled &&
      this.activationJourney.getState().privateEvaluationActive
    ) {
      void this.router.navigate(['/setup/example-workday']);
      return;
    }

    if (enable && this.router.url.startsWith('/setup')) {
      void this.router.navigate(['/setup/example-workday']);
      return;
    }

    if (enable && !this.canShowOptionalHelpLinks()) {
      this.clearDashboardSampleOptIn();
      this.router.navigate(['/dashboard']);
      return;
    }

    if (enable) {
      localStorage.setItem(this.demoDashboardOptInKey, 'true');
    } else {
      this.clearDashboardSampleOptIn();
    }
    this.sharedServices.requestDashboardDemoOptInRefresh();
    this.router.navigate(['/dashboard']);
  }

  /** @deprecated Use tryDemoData(enable) — kept for call-site clarity aliases. */
  setDashboardSampleOptIn(enable: boolean): void {
    this.tryDemoData(enable);
  }

  startProductTour(): void {
    this.projectService.getUser().subscribe({
      next: (data: any) => {
        const roleArr = data?.role || [];
        const adminRole = roleArr.filter(
          (ele: string) =>
            ele.toLowerCase().includes('admin') || ele.toLowerCase().includes('company')
        );
        const isNotAdmin = adminRole.length === 0;
        localStorage.setItem('isTour', 'true');
        localStorage.setItem('tourSkipped', 'false');
        this.projectService.updateDashboardTourData({ data: 'true' });
        this.sharedServices.sideMenuSubject.next(true);
        const startTour = () => {
          this.tourConfigService.getTourData(isNotAdmin);
          this.tourMdMenuService.start();
        };
        if (this.router.url.startsWith('/dashboard')) {
          setTimeout(startTour, 400);
        } else {
          void this.router.navigate(['/dashboard']).then(() => setTimeout(startTour, 400));
        }
      },
      error: () => {
        this.sweetAlertService.showAlert('Tour', 'Unable to start tour right now.', 'error');
      },
    });
  }
}
